import React, { useState, useEffect, useId, useMemo } from 'react';

import { getCodeList } from 'country-list';
import { getCountryCallingCode, isValidPhoneNumber, isPossiblePhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { useTranslation } from 'react-i18next';
import countries from 'i18n-iso-countries';
import 'i18n-iso-countries/langs/de.json';
import ReactCountryFlag from 'react-country-flag';

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
} from "@/components/ui/select";

interface PhoneInputProps {
    id?: string;
    value?: string;
    onChange?: (value: string) => void;
    onValidationChange?: (isValid: boolean) => void;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
    errorMessage?: string;
    className?: string;
    defaultCountry?: string; // ISO 3166-1 alpha-2 country code (e.g., 'DE', 'US')
    showValidation?: boolean;
}

interface CountryOption {
    key: string;
    text: string;
    callingCode: string;
    flag?: string;
    data?: any;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
    id,
    value = '',
    onChange,
    onValidationChange,
    label,
    required = false,
    disabled = false,
    placeholder,
    errorMessage: _errorMessage,
    className,
    defaultCountry = 'DE',
    showValidation = true,
}) => {
    const { t } = useTranslation();
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const [selectedCountry, setSelectedCountry] = useState<string>(defaultCountry);
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [_validationError, setValidationError] = useState<string>('');
    const [isValid, setIsValid] = useState<boolean>(false);

    // Initialize i18n-iso-countries with German
    countries.registerLocale(require('i18n-iso-countries/langs/de.json'));

    // Get country data
    const countryList = getCodeList();

    // Create country options with calling codes
    const countryOptions: CountryOption[] = useMemo(() => {
        return Object.keys(countryList).map((countryCode) => {
            // Get German country name
            const germanCountryName = countries.getName(countryCode.toUpperCase(), 'de') || countryList[countryCode];
            let callingCode = '';

            try {
                callingCode = getCountryCallingCode(countryCode.toUpperCase() as any);
            } catch (error) {
                // Some countries might not have calling codes in libphonenumber-js
                callingCode = '';
            }

            return {
                key: countryCode.toUpperCase(),
                text: `${germanCountryName} (+${callingCode})`,
                callingCode,
                flag: countryCode.toUpperCase(),
                data: {
                  countryCode: countryCode.toUpperCase(),
                  flag: countryCode.toUpperCase(),
                }
            };
        }).filter(option => option.callingCode) // Only include countries with calling codes
          .sort((a, b) => a.text.localeCompare(b.text, 'de'));
    }, [countryList]);

    // Parse initial value if provided
    useEffect(() => {
        if (value) {
            try {
                const parsed = parsePhoneNumber(value);
                if (parsed && parsed.country) {
                    setSelectedCountry(parsed.country);
                    setPhoneNumber(parsed.nationalNumber || '');
                }
            } catch (error) {
                // If parsing fails, try to extract country code from the beginning
                const match = value.match(/^\+(\d+)\s*(.+)$/);
                if (match) {
                    const [, callingCode, number] = match;
                    const country = countryOptions.find(opt => opt.callingCode === callingCode);
                    if (country) {
                        setSelectedCountry(country.key as string);
                        setPhoneNumber(number);
                    }
                }
            }
        }
    }, [value, countryOptions]);

    // Validate phone number
    useEffect(() => {
        if (!phoneNumber.trim()) {
            setValidationError('');
            setIsValid(false);
            onValidationChange?.(false);
            return;
        }

        const fullNumber = `+${getCountryCallingCode(selectedCountry as any)}${phoneNumber}`;

        let error = '';
        let valid = false;

        try {
            if (!isPossiblePhoneNumber(fullNumber)) {
                error = t('phoneInput.invalidFormat', 'Invalid phone number format');
            } else if (!isValidPhoneNumber(fullNumber)) {
                error = t('phoneInput.invalidNumber', 'Invalid phone number');
            } else {
                valid = true;
            }
        } catch (_error) {
            error = t('phoneInput.invalidFormat', 'Invalid phone number format');
        }

        setValidationError(error);
        setIsValid(valid);
        onValidationChange?.(valid);
    }, [phoneNumber, selectedCountry, onValidationChange, t]);

    // Update parent component when value changes
    useEffect(() => {
        if (phoneNumber && selectedCountry) {
            try {
                const fullNumber = `+${getCountryCallingCode(selectedCountry as any)}${phoneNumber}`;
                onChange?.(fullNumber);
            } catch (error) {
                // Handle error silently
            }
        } else {
            onChange?.('');
        }
    }, [phoneNumber, selectedCountry, onChange]);

    const handleCountryChange = (countryCode: string) => {
        if (countryCode) {
            setSelectedCountry(countryCode);
        }
    };

    const handlePhoneNumberChange = (newValue?: string) => {
        // Only allow digits, spaces, dashes, and parentheses
        const cleanedValue = newValue?.replace(/[^\d\s\-()]/g, '') || '';
        setPhoneNumber(cleanedValue);
    };

    const selectedCountryOption = countryOptions.find(opt => opt.key === selectedCountry);
    const selectCountryLabel = t('phoneInput.selectCountry', 'Select country');

    return (
        <div className={className}>
            {label && (
                <Label htmlFor={inputId} className="mb-1.5 block">
                    {label}
                    {required && <span aria-hidden="true" className="ml-0.5 text-destructive">*</span>}
                </Label>
            )}
            {/* Intrinsisch: die Vorwahl behält ihre Breite, das Nummernfeld wächst mit
                und darf schrumpfen (min-w-0); auf schmalen Viewports bricht die Zeile um. */}
            <div className="flex flex-wrap items-end gap-2">
                <Select value={selectedCountry} onValueChange={handleCountryChange} disabled={disabled}>
                    <SelectTrigger className="w-[92px] shrink-0" aria-label={selectCountryLabel}>
                        {selectedCountryOption
                            ? (
                                <span className="flex items-center gap-1.5">
                                    <ReactCountryFlag countryCode={selectedCountryOption.key} svg className="h-[1.5em] w-[1.5em]" />
                                    <span>+{selectedCountryOption.callingCode}</span>
                                </span>
                            )
                            : <span className="text-muted-foreground">{selectCountryLabel}</span>}
                    </SelectTrigger>
                    <SelectContent className="max-h-72 w-80 max-w-[calc(100vw-2rem)]">
                        {countryOptions.map((option) => (
                            <SelectItem key={option.key} value={option.key} textValue={option.text}>
                                <span className="flex items-center gap-2">
                                    <ReactCountryFlag countryCode={option.key} svg className="h-[1.5em] w-[1.5em]" />
                                    <span>{option.text}</span>
                                </span>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Input
                    id={inputId}
                    className="min-w-0 flex-1 basis-48"
                    value={phoneNumber}
                    onChange={(event) => handlePhoneNumberChange(event.target.value)}
                    disabled={disabled}
                    placeholder={placeholder || t('phoneInput.enterPhoneNumber', 'Enter phone number')}
                />
            </div>
            {showValidation && phoneNumber && (
                <div className={cn('mt-1 text-xs', isValid ? 'text-success' : 'text-destructive')}>
                    {isValid
                        ? t('phoneInput.validNumber', '✓ Valid phone number')
                        : t('phoneInput.invalidNumber', '✗ Invalid phone number')
                    }
                </div>
            )}
        </div>
    );
};

export default PhoneInput;
